/**
 * Formatting for LLM usage costs. Per-message costs are tiny (often
 * fractions of a cent) and shown discreetly; cumulative totals read as
 * ordinary dollar amounts.
 */

/**
 * Compact per-message/per-job form. Returns null for zero/unknown costs
 * so callers can skip rendering entirely.
 *   $0.04   (>= 1 cent: two decimals)
 *   $0.0004 (< 1 cent: two significant digits, max 4 decimals)
 */
export function formatCostCompact(
  costUsd: number | null | undefined,
): string | null {
  if (costUsd === null || costUsd === undefined || costUsd <= 0) return null;
  if (costUsd >= 0.01) return `$${costUsd.toFixed(2)}`;
  const rounded = Number(costUsd.toPrecision(2));
  if (rounded < 0.0001) return '$0.0001';
  return `$${rounded.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
}

/**
 * Cumulative/headline form distinguishes unknown cost from a known zero.
 *   $4.37, $0.00, —, and <$0.01 for tiny non-zero totals.
 */
export function formatCostTotal(costUsd: number | null | undefined): string {
  if (costUsd === null || costUsd === undefined) return '—';
  if (costUsd <= 0) return '$0.00';
  if (costUsd < 0.005) return '<$0.01';
  return `$${costUsd.toFixed(2)}`;
}

/**
 * A share of a total, as a percent to one decimal: '31.4%'. Unknown reads as
 * the same dash the cost and token totals use, because a share nobody
 * computed is not a share of nothing.
 */
export function formatSharePercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return `${Math.max(value, 0).toFixed(1)}%`;
}

/**
 * Native-title tooltip text for a per-message cost, e.g.
 * "1,234 tokens in · 567 out · $0.000412"
 */
export function costTooltip(
  tokensSent: number | null | undefined,
  tokensReceived: number | null | undefined,
  costUsd: number | null | undefined,
): string {
  const parts: string[] = [];
  if (tokensSent !== null && tokensSent !== undefined) {
    parts.push(`${tokensSent.toLocaleString()} tokens in`);
  }
  if (tokensReceived !== null && tokensReceived !== undefined) {
    parts.push(`${tokensReceived.toLocaleString()} out`);
  }
  if (costUsd !== null && costUsd !== undefined && costUsd > 0) {
    parts.push(`$${costUsd.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`);
  }
  return parts.join(' · ');
}
